import { z } from 'zod/v3';

// ============================================================================
// Enum 스키마
// ============================================================================

/**
 * 채팅 메시지 발신자 유형
 * - BUYER: 구매자 (사장님)
 * - PARTNER: 파트너 (세무사)
 * - SYSTEM: 시스템 메시지 (SSE 전용, REST API 미포함)
 */
export const chatSenderTypeSchema = z.enum(['BUYER', 'PARTNER', 'SYSTEM']);
export type ChatSenderType = z.infer<typeof chatSenderTypeSchema>;

// ============================================================================
// Object 스키마
// ============================================================================

/**
 * 채팅 메시지 첨부파일 스키마
 * @see ChatAttachmentResponse
 */
export const chatMessageAttachmentSchema = z.object({
  /** 파일 ID */
  id: z.string(),
  /** 파일명 */
  fileName: z.string(),
  /** 파일 크기 (bytes) */
  fileSize: z.number(),
  /** MIME 타입 (예: image/jpeg, application/pdf) */
  mimeType: z.string(),
  /** S3 저장 키 */
  s3Key: z.string(),
});
export type ChatMessageAttachment = z.infer<typeof chatMessageAttachmentSchema>;

/**
 * 채팅 메시지 스키마
 * @see ChatMessageResponse
 * @see GET /api/market-place/chat/rooms/{roomId}/messages
 */
export const chatMessageSchema = z.object({
  /** 메시지 ID */
  id: z.number(),
  /** 채팅방 ID */
  roomId: z.string(),
  /** 발신자 ID */
  senderId: z.string(),
  /** 발신자 유형 */
  senderType: chatSenderTypeSchema,
  /** 발신자 이름 */
  senderName: z.string(),
  /** 메시지 내용 */
  content: z.string(),
  /** 메시지 생성 일시 (ISO 8601) */
  createdAt: z.string(),
  /** 첨부파일 목록 */
  attachments: z.array(chatMessageAttachmentSchema),
});
export type ChatMessage = z.infer<typeof chatMessageSchema>;

/**
 * 채팅 메시지 목록 응답 스키마 (API는 배열을 직접 반환, repository에서 래핑)
 */
export const chatMessageListSchema = z.object({
  /** 메시지 목록 */
  messages: z.array(chatMessageSchema),
});
export type ChatMessageList = z.infer<typeof chatMessageListSchema>;

/**
 * 채팅방 스키마
 * @see ChatRoomResponse
 * @see POST /api/market-place/chat/rooms
 */
export const chatRoomSchema = z.object({
  /** 채팅방 ID */
  id: z.number(),
  /** 구매자 ID */
  buyerId: z.number(),
  /** 파트너 ID */
  partnerId: z.number(),
  /** 게스트 URL */
  guestUrl: z.string(),
  /** 안 읽은 메시지 수 */
  unreadCount: z.number(),
  /** 생성 일시 */
  createdAt: z.string(),
});
export type ChatRoom = z.infer<typeof chatRoomSchema>;

/**
 * 파일 업로드 응답 스키마 (presigned URL)
 * @see UploadUrlResponse
 * @see POST /api/market-place/chat/files/upload
 */
export const uploadFileResponseSchema = z.object({
  /** 파일 ID */
  fileId: z.string(),
  /** S3 presigned URL (PUT 업로드용) */
  presignedUrl: z.string(),
  /** S3 저장 키 */
  s3Key: z.string(),
});
export type UploadFileResponse = z.infer<typeof uploadFileResponseSchema>;

/**
 * 알람 채팅방 스키마
 * @see AlarmChatRoomResponse
 * @see GET /api/market-place/chat/rooms/alarm
 */
export const alarmChatRoomSchema = z.object({
  /** 채팅방 ID */
  chatRoomId: z.number(),
  /**
   * 채팅방 생성 일시 (yyyyMMddHHmmss 형식)
   * @example 20250731150000
   */
  createdAt: z.string(),
  /** 마지막 메시지 내용 */
  lastMessage: z.string(),
  /**
   * 마지막 메시지 발송 일시 (yyyyMMddHHmmss 형식)
   * @example 20250731150000
   */
  lastMessageAt: z.string().nullish(),
  /** 파트너 사무소명 */
  officeName: z.string(),
  /** 파트너 ID */
  partnerId: z.number(),
  /** 파트너 프로필 이미지 URL */
  profileImageUrl: z.string(),
  /** 안 읽은 메시지 수 */
  unreadCount: z.number(),
});
export type AlarmChatRoom = z.infer<typeof alarmChatRoomSchema>;

/**
 * 메시지 전송 응답 스키마
 * @see POST /api/market-place/chat/messages
 */
export const sendMessageResponseSchema = chatMessageSchema;
export type SendMessageResponse = z.infer<typeof sendMessageResponseSchema>;
